import type {
	ShadowFloor,
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../types/location-map";

import {
	CreateEntityModal,
	type CreateEntityType,
	EditEntityModal,
	type EditEntityTarget,
	type EditEntityType,
} from "./modals";

type LocationManagementModalsProps = {
	createModal: CreateEntityType | null;
	createSensorZoneId: null | string;
	editTarget: EditEntityTarget | null;
	isLocationSelected: boolean;
	location: ShadowLocation;
	locations: ShadowLocation[];
	mapCenter: [number, number];
	onCloseCreate: () => void;
	onCloseEdit: () => void;
	onLocationCreated: (locationId: string) => void;
	onSelectSensor?: (sensorId: string) => void;
};

const resolveEditEntityTarget = (
	locations: ShadowLocation[],
	target: EditEntityTarget | null,
): {
	floor: null | ShadowFloor;
	location: null | ShadowLocation;
	sensor: null | ShadowSensor;
	type: EditEntityType | null;
	zone: null | ShadowZone;
} => {
	if (!target) {
		return {
			floor: null,
			location: null,
			sensor: null,
			type: null,
			zone: null,
		};
	}

	const location =
		locations.find((entry) => entry.id === target.locationId) ?? null;
	if (!location) {
		return {
			floor: null,
			location: null,
			sensor: null,
			type: target.type,
			zone: null,
		};
	}

	if (target.type === "location") {
		return {
			floor: null,
			location,
			sensor: null,
			type: "location",
			zone: null,
		};
	}

	if (target.type === "floor") {
		const floor =
			(location.floors ?? []).find((entry) => entry.id === target.floorId) ??
			null;
		return { floor, location, sensor: null, type: "floor", zone: null };
	}

	if (target.type === "zone") {
		const zone =
			(location.zones ?? []).find((entry) => entry.id === target.zoneId) ??
			null;
		return { floor: null, location, sensor: null, type: "zone", zone };
	}

	let sensor: null | ShadowSensor = null;
	for (const entry of location.zones ?? []) {
		const match = (entry.sensors ?? []).find(
			(item) => item.id === target.sensorId,
		);
		if (match) {
			sensor = match;
			break;
		}
	}
	return { floor: null, location, sensor, type: "sensor", zone: null };
};

export const LocationManagementModals = ({
	createModal,
	createSensorZoneId,
	editTarget,
	isLocationSelected,
	location,
	locations,
	mapCenter,
	onCloseCreate,
	onCloseEdit,
	onLocationCreated,
	onSelectSensor,
}: LocationManagementModalsProps) => {
	const edit = resolveEditEntityTarget(locations, editTarget);

	return (
		<>
			<CreateEntityModal
				defaultZoneId={createSensorZoneId}
				location={isLocationSelected ? location : null}
				mapCenter={mapCenter}
				onClose={onCloseCreate}
				onLocationCreated={onLocationCreated}
				type={createModal}
			/>
			<EditEntityModal
				floor={edit.floor}
				location={edit.location}
				mapCenter={mapCenter}
				onClose={onCloseEdit}
				onSelectSensor={onSelectSensor}
				sensor={edit.sensor}
				type={edit.type}
				zone={edit.zone}
			/>
		</>
	);
};
